import React, { useCallback, useState, useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  SafeAreaView,
  Platform,
  Dimensions,
} from 'react-native';
import type { FullGridCalendarProps, CalendarViewMode } from './types';
import type { CalendarEvent } from './types';
import { DEFAULT_THEME, DEFAULT_STYLE_CONFIG, DEFAULT_BEHAVIOR_CONFIG, WEEKDAYS } from './constants';
import {
  useCalendarNavigation,
  useCalendarViewMode,
  useCalendarDates,
  useEventManagement,
  useDateUtils,
} from './hooks';
import {
  getEventBadgeColor,
  getEventTextColor,
  generateCalendarGrid,
  getPreviousMonthInfo,
} from './utils';
import { styles } from './styles';
import { ControlBar } from './components/ControlBar';
import { MonthlyView } from './components/MonthlyView';
import { WeeklyView } from './components/WeeklyView';
import { EventDetailsPopover } from './components/EventDetailsPopover';
import { EventSummaryModal } from './components/EventSummaryModal';

// Re-export types for external use
export type { FullGridCalendarProps, CalendarEvent, CalendarTheme, CalendarViewMode, CalendarStyleConfig, CalendarBehaviorConfig } from './types';
export { DEFAULT_THEME, DEFAULT_STYLE_CONFIG, DEFAULT_BEHAVIOR_CONFIG } from './constants';

/**
 * FullGridCalendar Component
 * A comprehensive grid-based calendar with monthly and weekly views
 *
 * Based on Figma design: Warehouse Navigator
 * Features:
 * - Monthly view: Full month grid (7 columns x 5-6 rows)
 * - Weekly view: Single week (7 columns x 1 row)
 * - Event badges for each day with color coding
 * - Touch-enabled date selection
 * - Month/week navigation
 * - Customizable theming
 * - View mode switcher (Weekly/Monthly)
 * - Fully configurable styling and behavior
 */
export const FullGridCalendar: React.FC<FullGridCalendarProps> = ({
  events = [],
  onDateSelect,
  onEventPress,
  onEventRemove,
  onEventDetailsPress,
  onShowAllEvents,
  onNavigationChange,
  onViewModeChange,
  onViewDetails,
  theme = {},
  styleConfig = {},
  behaviorConfig = {},
  showWeekDays = true,
  containerStyle,
  viewMode = 'monthly',
  isLoading = false,
}) => {
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [summaryDate, setSummaryDate] = useState<Date | null>(null);
  const [summaryEvents, setSummaryEvents] = useState<CalendarEvent[]>([]);
  const [summaryModalVisible, setSummaryModalVisible] = useState(false);
  
  // Merge configurations with defaults
  const mergedTheme = useMemo(() => ({ ...DEFAULT_THEME, ...theme }), [theme]);
  const mergedStyleConfig = useMemo(() => ({ ...DEFAULT_STYLE_CONFIG, ...styleConfig }), [styleConfig]);
  const mergedBehaviorConfig = useMemo(() => ({ ...DEFAULT_BEHAVIOR_CONFIG, ...behaviorConfig }), [behaviorConfig]);

  // Use custom hooks
  const { currentDate, goToPreviousMonth, goToNextMonth, goToPreviousWeek, goToNextWeek } =
    useCalendarNavigation();
  
  // Force weekly mode on iOS mobile devices
  const { width: SCREEN_WIDTH } = Dimensions.get('window');
  const isIOSMobile = Platform.OS === 'ios' && SCREEN_WIDTH < 768;
  const forcedViewMode = isIOSMobile ? 'weekly' : viewMode;
  
  const { activeViewMode, handleViewModeChange: setViewMode } =
    useCalendarViewMode(forcedViewMode);
  const { firstDay, daysInMonth, startingDayOfWeek } = useCalendarDates(currentDate);
  const { getEventsForDate } = useEventManagement(events);
  const { isDateToday, getWeekDates } = useDateUtils();

  // Enhanced view mode change handler
  const handleViewModeChange = useCallback(
    (mode: CalendarViewMode) => {
      setViewMode(mode);
      // Call both callbacks - internal state update and parent callback
      if (onViewModeChange && mode !== activeViewMode) {
        onViewModeChange(mode);
      }
    },
    [setViewMode, activeViewMode, onViewModeChange]
  );

  // Wrapper for previous navigation to trigger parent callback
  const handleGoToPreviousMonth = useCallback(() => {
    goToPreviousMonth();
    // Calculate the new date after going to previous month
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
    onNavigationChange?.(newDate, 'monthly');
  }, [goToPreviousMonth, currentDate, onNavigationChange]);

  const handleGoToNextMonth = useCallback(() => {
    goToNextMonth();
    // Calculate the new date after going to next month
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1);
    onNavigationChange?.(newDate, 'monthly');
  }, [goToNextMonth, currentDate, onNavigationChange]);

  const handleGoToPreviousWeek = useCallback(() => {
    const prevWeek = new Date(currentDate);
    prevWeek.setDate(prevWeek.getDate() - 7);
    goToPreviousWeek();
    onNavigationChange?.(prevWeek, 'weekly');
  }, [goToPreviousWeek, currentDate, onNavigationChange]);

  const handleGoToNextWeek = useCallback(() => {
    const nextWeek = new Date(currentDate);
    nextWeek.setDate(nextWeek.getDate() + 7);
    goToNextWeek();
    onNavigationChange?.(nextWeek, 'weekly');
  }, [goToNextWeek, currentDate, onNavigationChange]);

  // Handle date selection
  const handleDatePress = useCallback(
    (day: number) => {
      const selectedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      onDateSelect?.(selectedDate);
    },
    [currentDate, onDateSelect]
  );

  // Handle event details press
  const handleEventDetailsPress = useCallback(
    (event: CalendarEvent) => {
      if (mergedBehaviorConfig.singleEventClickBehavior === 'callback' || mergedBehaviorConfig.singleEventClickBehavior === 'both') {
        onEventDetailsPress?.(event);
      }
      if (mergedBehaviorConfig.singleEventClickBehavior === 'popover' || mergedBehaviorConfig.singleEventClickBehavior === 'both') {
        setSelectedEvent(event);
        setPopoverVisible(true);
      }
    },
    [onEventDetailsPress, mergedBehaviorConfig]
  );

  // Handle show all events
  const handleShowAllEvents = useCallback(
    (date: Date, eventsForDate: CalendarEvent[]) => {
      if (mergedBehaviorConfig.multipleEventClickBehavior === 'callback' || mergedBehaviorConfig.multipleEventClickBehavior === 'both') {
        onShowAllEvents?.(date, eventsForDate);
      }
      if (mergedBehaviorConfig.multipleEventClickBehavior === 'modal' || mergedBehaviorConfig.multipleEventClickBehavior === 'both') {
        setSummaryDate(date);
        setSummaryEvents(eventsForDate);
        setSummaryModalVisible(true);
      }
    },
    [onShowAllEvents, mergedBehaviorConfig]
  );

  // Render event badge
  const renderEventBadge = (event: CalendarEvent, small = false) => {
    const bgColor = getEventBadgeColor(event, mergedTheme);
    const textColor = getEventTextColor(event);

    return (
      <View
        key={event.id}
        style={[
          styles.eventBadge,
          small && styles.eventBadgeSmall,
          { 
            backgroundColor: bgColor, 
            flexDirection: 'row', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            borderRadius: mergedStyleConfig.eventBadgeRadius,
            height: mergedStyleConfig.eventBadgeHeight,
          },
        ]}
      >
        <Text
          style={[
            styles.eventText,
            small && styles.eventTextSmall,
            { color: textColor, flex: 1, fontSize: mergedStyleConfig.eventBadgeFontSize, textTransform: 'capitalize' },
          ]}
          numberOfLines={1}
        >
          {event.title}
        </Text>
        {mergedBehaviorConfig.enableEventRemoval && event.removable !== false && (
          <TouchableOpacity
            onPress={() => onEventRemove?.(event.id)}
            style={{ padding: 0, marginLeft: 4 }}
            hitSlop={{ top: 5, bottom: 5, left: 5, right: 5 }}
          >
            <Text style={{ fontSize: 16, fontWeight: 'bold', color: textColor }}>×</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  return (
    <SafeAreaView
      style={[
        styles.container,
        { backgroundColor: mergedTheme.backgroundColor },
        containerStyle,
      ]}
    >
      {/* Control Bar - View Switcher and Navigation */}
      {mergedBehaviorConfig.enableViewModeSwitch && (
        <ControlBar
          activeViewMode={activeViewMode}
          currentDate={currentDate}
          onViewModeChange={handleViewModeChange}
          onPreviousClick={
            activeViewMode === 'weekly' ? handleGoToPreviousWeek : handleGoToPreviousMonth
          }
          onNextClick={activeViewMode === 'weekly' ? handleGoToNextWeek : handleGoToNextMonth}
          theme={mergedTheme}
          getWeekDates={getWeekDates}
        />
      )}

      {/* Weekday headers for monthly view */}
      {activeViewMode === 'monthly' && showWeekDays && mergedBehaviorConfig.enableWeekdayHeaders && (
        <View style={styles.weekdaysRow}>
          {WEEKDAYS.map((day) => (
            <View key={day} style={styles.weekdayCell}>
              <Text
                style={[
                  styles.weekdayText,
                  { color: mergedTheme.headerTextColor },
                ]}
              >
                {day}
              </Text>
            </View>
          ))}
        </View>
      )}

      {/* Calendar Content */}
      {activeViewMode === 'monthly' ? (
        <MonthlyView
          firstDay={firstDay}
          daysInMonth={daysInMonth}
          startingDayOfWeek={startingDayOfWeek}
          currentDate={currentDate}
          getEventsForDate={getEventsForDate}
          isDateToday={isDateToday}
          onDatePress={handleDatePress}
          onEventDetailsPress={handleEventDetailsPress}
          onEventPress={onEventPress}
          onShowAllEvents={handleShowAllEvents}
          renderEventBadge={renderEventBadge}
          theme={mergedTheme}
          generateCalendarGrid={generateCalendarGrid}
          getPreviousMonthInfo={getPreviousMonthInfo}
          styleConfig={mergedStyleConfig}
          isLoading={isLoading}
        />
      ) : (
        <WeeklyView
          currentDate={currentDate}
          getEventsForDate={getEventsForDate}
          isDateToday={isDateToday}
          onDatePress={handleDatePress}
          onEventDetailsPress={handleEventDetailsPress}
          onEventPress={onEventPress}
          onShowAllEvents={handleShowAllEvents}
          renderEventBadge={renderEventBadge}
          theme={mergedTheme}
          getWeekDates={getWeekDates}
          styleConfig={mergedStyleConfig}
          isLoading={isLoading}
        />
      )}

      {/* Event Details Popover */}
      <EventDetailsPopover
        visible={popoverVisible}
        event={selectedEvent}
        eventData={selectedEvent?.actionPlanData}
        onClose={() => setPopoverVisible(false)}
        onViewDetails={(event) => {
          setPopoverVisible(false);
          onViewDetails?.(event);
        }}
        styleConfig={mergedStyleConfig}
      />

      {/* Event Summary Modal */}
      <EventSummaryModal
        visible={summaryModalVisible}
        events={summaryEvents}
        date={summaryDate}
        onClose={() => setSummaryModalVisible(false)}
        onEventPress={handleEventDetailsPress}
        onViewDetails={(event) => {
          setSummaryModalVisible(false);
          onViewDetails?.(event);
        }}
        styleConfig={mergedStyleConfig}
      />
    </SafeAreaView>
  );
};

export default FullGridCalendar;
